// react-router-dom components
import { Link } from "react-router-dom";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Images
import curved9 from "assets/images/haazri-logo.jpg";

// Custom Imports
import { useForm } from "react-hook-form";
import { signInSchema } from "./sign-in.constants";
import { yupResolver } from "@hookform/resolvers/yup";
import ControlledInputField from "components/ControlledInputField";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { signIn, fetchAuthOptions, startSlackSignIn } from "../auth.thunk";
import { useEffect, useState } from "react";
import ErrorAlert from "components/ErrorAlert";
import { resetMessageState } from "../auth.reducer";
import { CircularProgress } from "@mui/material";
import SlackLogo from "../components/SlackLogo";

function SignIn() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { formState: { errors }, control, handleSubmit } = useForm({
    resolver: yupResolver(signInSchema),
  });

  // Store
  const { loading, error } = useSelector((state) => state.auth)

  // Until the server answers, offer only the password form, so a failed call
  // never shows a method that doesn't work.
  const [options, setOptions] = useState({ password_login: true, slack_sso: false });
  const [slackStarting, setSlackStarting] = useState(false);
  const [slackError, setSlackError] = useState(null);

  useEffect(() => {
    dispatch(fetchAuthOptions())
      .unwrap()
      .then(setOptions)
      .catch(() => {});
  }, []);

  function onSlackSignIn() {
    setSlackError(null);
    setSlackStarting(true);
    dispatch(startSlackSignIn())
      .unwrap()
      .catch((err) => {
        setSlackStarting(false);
        setSlackError(err.message);
      });
  }

  function onSubmit(data) {
    dispatch(signIn(data));
  }

  useEffect(() => {
    dispatch(resetMessageState());
  }, [pathname]);

  function clearError() {
    dispatch(resetMessageState());
    setSlackError(null);
  }

  return (
    <CoverLayout
      title="Haazri Admin Portal"
      description={
        options.password_login
          ? "Enter your email and password to sign in"
          : "Sign in with your Slack account"
      }
      image={curved9}
    >
      {(error || slackError) && (
        <ErrorAlert color="error" message={error || slackError} clearError={clearError} />
      )}
      {options.password_login && (
        <SuiBox component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
          <SuiBox>
            <SuiBox mb={1} ml={0.5}>
              <SuiTypography component="label" variant="caption" fontWeight="bold">
                Email
              </SuiTypography>
            </SuiBox>
            <ControlledInputField type="email" placeholder="Enter Email" name="email" control={control} errors={errors} />
          </SuiBox>
          <SuiBox mb={2}>
            <SuiBox mb={1} ml={0.5}>
              <SuiTypography component="label" variant="caption" fontWeight="bold">
                Password
              </SuiTypography>
            </SuiBox>
            <ControlledInputField type="password" placeholder="Enter Password" name="password" control={control} errors={errors} />
          </SuiBox>
          <SuiBox mt={4} mb={1}>
            <SuiButton variant="gradient" color="info" fullWidth type="submit" disabled={loading}>
              {loading ? <CircularProgress color="white" size={17} /> : "sign in"}
            </SuiButton>
          </SuiBox>
          {/* <SuiBox mt={3} textAlign="center">
            <SuiTypography variant="button" color="text" fontWeight="regular">
              Don&apos;t have an account?{" "}
              <SuiTypography
                component={Link}
                to="/authentication/sign-up"
                variant="button"
                fontWeight="medium"
                textGradient
              >
                Sign up
              </SuiTypography>
            </SuiTypography>
          </SuiBox> */}
        </SuiBox>
      )}
      {options.slack_sso && (
        <SuiBox>
          {options.password_login && (
            <SuiBox mt={2} mb={2} textAlign="center">
              <SuiTypography variant="caption" color="text">
                or
              </SuiTypography>
            </SuiBox>
          )}
          <SuiButton
            variant="outlined"
            color="dark"
            fullWidth
            onClick={onSlackSignIn}
            disabled={slackStarting}
          >
            {slackStarting ? (
              <CircularProgress color="inherit" size={17} />
            ) : (
              <>
                <SlackLogo />
                &nbsp;&nbsp;Sign in with Slack
              </>
            )}
          </SuiButton>
        </SuiBox>
      )}
    </CoverLayout>
  );
}

export default SignIn;
